import type { CSSProperties } from "react";

import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { XIcon } from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import type { Identity, OpenAPI } from "simplydo/interfaces";
import { css, styled } from "simplydo/react/styled";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label, LabelDetail } from "simplydo/react/ui/label";
import { Popup, PopupContent } from "simplydo/react/ui/popup";

export const TagContainer = styled.div<{ $noMargin?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  min-width: 0;
  margin-top: ${({ $noMargin }) => (!$noMargin ? "5px" : "0px")};
  border-radius: 5px;
  &&& > * {
    margin: ${({ $noMargin }) => (!$noMargin ? "5px" : "0px")};
    max-width: 100%;
    min-width: 0;
  }
`;

const TagLink = styled(Link)`
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
`;

const TagTrigger = styled.div`
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
`;

const wrapLabelStyle: CSSProperties = {
  whiteSpace: "normal",
  height: "auto",
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const disabledSimpleTagClass = css`
  color: rgba(16, 16, 16, 0.3) !important;
  background-color: rgba(239, 239, 239, 0.3) !important;
  cursor: default;
`;

export type TagLike = Pick<OpenAPI.Schemas["Tag"], "_id" | "value"> & {
  description?: string;
  totalCount?: number;
};

type TagComponentProps = {
  tag: TagLike;
  disabled?: boolean;
  onRemove?: (tag: TagLike) => void;
  showCount?: boolean;
  onClick?: (tag: TagLike) => void;
  noRedirect?: boolean;
};

const TagComponent = ({
  tag,
  disabled,
  onRemove,
  showCount,
  onClick,
  noRedirect,
}: TagComponentProps) => {
  const navigate = useNavigate();

  if (!tag) {
    return null;
  }

  const onClickLabel = (e: React.UIEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }
    if (onClick) {
      onClick(tag);
    }
    if (!noRedirect) {
      navigate(`/tags/${tag._id}`);
    }
  };

  const handleRemove = onRemove
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
          onRemove(tag);
        }
      }
    : undefined;
  const content = (
    <>
      {showCount && "totalCount" in tag
        ? `${tag.value} (${tag.totalCount})`
        : tag.value}
      {handleRemove ? (
        <LabelDetail onClick={handleRemove}>
          <Icon icon={XIcon} />
        </LabelDetail>
      ) : null}
    </>
  );

  const labelProps = {
    role: "button" as const,
    tabIndex: 0,
    "aria-disabled": disabled,
    size: "medium" as const,
    pointing: "left" as const,
    onClick: onClickLabel,
    onKeyDown: handleClickForKeyboard(onClickLabel),
    style: { marginLeft: 2, ...wrapLabelStyle },
  };

  if (!disabled && (onClick || !noRedirect)) {
    return (
      <TagLink to={`/tags/${tag._id}`}>
        <Label {...labelProps}>{content}</Label>
      </TagLink>
    );
  }

  if (disabled) {
    return (
      <Label {...labelProps} className={disabledSimpleTagClass}>
        {content}
      </Label>
    );
  }

  return <Label {...labelProps}>{content}</Label>;
};

export type TagProps = Identity<TagComponentProps & { noPopup?: boolean }>;

export const Tag = ({ tag, noPopup, ...props }: TagProps) => {
  const { t } = useTranslation();
  if (!tag.description || noPopup) {
    return <TagComponent tag={tag} {...props} />;
  }
  return (
    <Popup
      trigger={
        <TagTrigger>
          <TagComponent tag={tag} {...props} />
        </TagTrigger>
      }
      on={["hover", "focus"]}
    >
      <PopupContent>
        <H5>{t("generic.aboutTags")}</H5>
        {tag.description}
      </PopupContent>
    </Popup>
  );
};

export default Tag;
